import type { StateCreator } from 'zustand'
import { logger } from '@/shared/lib/logger'
import { MOCK_PROFILES } from '../../constants'
import type {
  Agent,
  ContextProfile,
  Prompt,
  ScannerProfile,
} from '../../types'
import {
  getScanProfiles,
  getAnalysisPrompts,
  getAgentScanners,
  getAgentExecutors,
  type AgentScanner,
  type AgentExecutor,
  getAgentDefinitions,
  createAgentDefinition,
  updateAgentDefinition,
  deleteAgentDefinition,
  getAgentProviders,
  getAgentTools,
  getAgentTemplates,
  type AgentDefinition,
  type AgentDefinitionCreate,
  type AgentDefinitionUpdate,
  type ProviderInfo,
  type ToolInfo,
  type AgentTemplate,
  type WorkspaceAgent,
  getPrompts,
  createPrompt,
  updatePromptApi,
  deletePromptApi,
  getWorkspaceAgents,
  getAgentDefinitionsWithStatus,
  type AgentDefinitionWithStatus,
} from '@/shared/lib/api'
import type { AnalysisPrompt } from '@/shared/types'

export interface AgentCatalogSlice {
  agents: Agent[]
  profiles: ContextProfile[]
  prompts: Prompt[]
  promptsLoading: boolean
  promptsError: string | null
  scannerProfiles: ScannerProfile[]
  scannerProfilesLoading: boolean
  scannerProfilesError: string | null
  analysisPrompts: AnalysisPrompt[]
  analysisPromptsLoading: boolean
  agentScanners: AgentScanner[]
  agentScannersLoading: boolean
  agentExecutors: AgentExecutor[]
  agentExecutorsLoading: boolean
  agentDefinitions: AgentDefinition[]
  agentDefinitionsLoading: boolean
  agentDefinitionsError: string | null
  availableProviders: ProviderInfo[]
  availableProvidersLoading: boolean
  availableTools: ToolInfo[]
  availableToolsLoading: boolean
  agentTemplates: AgentTemplate[]
  agentTemplatesLoading: boolean
  workspaceAgents: WorkspaceAgent[]
  workspaceAgentsLoading: boolean
  workspaceAgentsError: string | null
  fetchAgents: () => Promise<void>
  setAgents: (agents: Agent[]) => void
  updateAgent: (id: string, updates: Partial<Agent>) => void
  addAgent: (agent: Agent) => void
  addProfile: (profile: ContextProfile) => void
  fetchScannerProfiles: () => Promise<void>
  addCustomScannerProfile: (profile: ScannerProfile) => void
  updateScannerProfile: (id: string, updates: Partial<ScannerProfile>) => void
  deleteScannerProfile: (id: string) => void
  fetchPrompts: (type?: string) => Promise<void>
  addPrompt: (data: { name: string; description?: string; content: string; tags?: string[]; type: string }) => Promise<Prompt>
  updatePrompt: (id: number, updates: Record<string, unknown>) => Promise<void>
  deletePrompt: (id: number) => Promise<void>
  savePrompt: (id: number, data: { name?: string; content?: string; description?: string; tags?: string[] }) => Promise<void>
  fetchAnalysisPrompts: () => Promise<void>
  fetchAgentScanners: () => Promise<void>
  fetchAgentExecutors: () => Promise<void>
  fetchAgentDefinitions: (agentType?: 'scanner' | 'executor' | 'general') => Promise<void>
  createAgentDefinition: (data: AgentDefinitionCreate) => Promise<AgentDefinition>
  updateAgentDefinition: (id: number, data: AgentDefinitionUpdate) => Promise<AgentDefinition>
  deleteAgentDefinition: (id: number) => Promise<void>
  fetchAvailableProviders: () => Promise<void>
  fetchAvailableTools: () => Promise<void>
  fetchAgentTemplates: () => Promise<void>
  fetchWorkspaceAgents: (workspacePath: string) => Promise<void>
}

export const createAgentCatalogSlice: StateCreator<
  AgentCatalogSlice,
  [],
  [],
  AgentCatalogSlice
> = (set, get) => ({
  agents: [],
  profiles: MOCK_PROFILES,
  prompts: [],
  promptsLoading: false,
  promptsError: null,
  scannerProfiles: [],
  scannerProfilesLoading: false,
  scannerProfilesError: null,
  analysisPrompts: [],
  analysisPromptsLoading: false,
  agentScanners: [],
  agentScannersLoading: false,
  agentExecutors: [],
  agentExecutorsLoading: false,
  agentDefinitions: [],
  agentDefinitionsLoading: false,
  agentDefinitionsError: null,
  availableProviders: [],
  availableProvidersLoading: false,
  availableTools: [],
  availableToolsLoading: false,
  agentTemplates: [],
  agentTemplatesLoading: false,
  workspaceAgents: [],
  workspaceAgentsLoading: false,
  workspaceAgentsError: null,
  fetchAgents: async () => {
    try {
      const agentDefinitions = await getAgentDefinitionsWithStatus(undefined, true)
      const mappedAgents: Agent[] = agentDefinitions.map((def: AgentDefinitionWithStatus) => ({
        id: `definition_${def.id}`,
        name: def.display_name || def.name,
        role: def.description || `${def.agent_type} agent`,
        status: def.status,
        avatar: def.avatar || (def.display_name || def.name).substring(0, 2).toUpperCase(),
        model: def.model_version || `${def.provider}-default`,
        systemPrompt: def.system_prompt || '',
        tools: def.tools || [],
        contextProfileId: def.context_profile_id?.toString(),
      }))
      set({ agents: mappedAgents })
    } catch (error) {
      logger.error('Failed to load agents', { error })
    }
  },
  setAgents: (agents) => set({ agents }),
  updateAgent: (id, updates) =>
    set((state) => ({
      agents: state.agents.map((a) => (a.id === id ? { ...a, ...updates } : a)),
    })),
  addAgent: (agent) => set((state) => ({ agents: [...state.agents, agent] })),
  addProfile: (profile) => set((state) => ({ profiles: [...state.profiles, profile] })),
  fetchScannerProfiles: async () => {
    if (get().scannerProfiles.length > 0 && !get().scannerProfilesError) return
    set({ scannerProfilesLoading: true, scannerProfilesError: null })
    try {
      const profiles = await getScanProfiles()
      const enrichedProfiles: ScannerProfile[] = profiles.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        prompt_id: '',
        strategy_type: p.strategy_type,
        strategy_config: {},
        icon: p.icon,
        category: p.category as ScannerProfile['category'],
        isBuiltIn: true,
      }))
      set({ scannerProfiles: enrichedProfiles, scannerProfilesLoading: false })
    } catch (error) {
      set({
        scannerProfilesError: error instanceof Error ? error.message : 'Failed to load profiles',
        scannerProfilesLoading: false,
      })
    }
  },
  addCustomScannerProfile: (profile) =>
    set((state) => ({
      scannerProfiles: [...state.scannerProfiles, { ...profile, isBuiltIn: false }],
    })),
  updateScannerProfile: (id, updates) =>
    set((state) => ({
      scannerProfiles: state.scannerProfiles.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    })),
  deleteScannerProfile: (id) =>
    set((state) => ({
      scannerProfiles: state.scannerProfiles.filter((p) => p.id !== id),
    })),
  fetchPrompts: async (type?: string) => {
    set({ promptsLoading: true, promptsError: null })
    try {
      const prompts = await getPrompts(type)
      set({ prompts, promptsLoading: false })
    } catch (error) {
      logger.error('Failed to load prompts', { error })
      set({
        promptsError: error instanceof Error ? error.message : 'Failed to load prompts',
        promptsLoading: false,
      })
    }
  },
  addPrompt: async (data) => {
    const newPrompt = await createPrompt(data)
    set((state) => ({ prompts: [...state.prompts, newPrompt] }))
    return newPrompt
  },
  updatePrompt: async (id, updates) => {
    const updated = await updatePromptApi(id, updates)
    set((state) => ({
      prompts: state.prompts.map((p) => (p.id === id ? updated : p)),
    }))
  },
  deletePrompt: async (id) => {
    await deletePromptApi(id)
    set((state) => ({
      prompts: state.prompts.filter((p) => p.id !== id),
    }))
  },
  savePrompt: async (id, data) => {
    const updated = await updatePromptApi(id, data as Record<string, unknown>)
    set((state) => ({
      prompts: state.prompts.map((p) => (p.id === id ? updated : p)),
    }))
  },
  fetchAnalysisPrompts: async () => {
    if (get().analysisPrompts.length > 0) return
    set({ analysisPromptsLoading: true })
    try {
      const prompts = await getAnalysisPrompts()
      set({ analysisPrompts: prompts, analysisPromptsLoading: false })
    } catch (error) {
      logger.error('Failed to load analysis prompts', { error })
      set({ analysisPromptsLoading: false })
    }
  },
  fetchAgentScanners: async () => {
    if (get().agentScanners.length > 0) return
    set({ agentScannersLoading: true })
    try {
      const scanners = await getAgentScanners()
      set({ agentScanners: scanners, agentScannersLoading: false })
    } catch (error) {
      logger.error('Failed to load agent scanners', { error })
      set({ agentScannersLoading: false })
    }
  },
  fetchAgentExecutors: async () => {
    if (get().agentExecutors.length > 0) return
    set({ agentExecutorsLoading: true })
    try {
      const executors = await getAgentExecutors()
      set({ agentExecutors: executors, agentExecutorsLoading: false })
    } catch (error) {
      logger.error('Failed to load agent executors', { error })
      set({ agentExecutorsLoading: false })
    }
  },
  fetchAgentDefinitions: async (agentType?: 'scanner' | 'executor' | 'general') => {
    set({ agentDefinitionsLoading: true, agentDefinitionsError: null })
    try {
      const definitions = await getAgentDefinitions(agentType)
      set({ agentDefinitions: definitions, agentDefinitionsLoading: false })
    } catch (error) {
      logger.error('Failed to load agent definitions', { error })
      set({
        agentDefinitionsError: error instanceof Error ? error.message : 'Failed to load agent definitions',
        agentDefinitionsLoading: false,
      })
    }
  },
  createAgentDefinition: async (data: AgentDefinitionCreate) => {
    try {
      const newDefinition = await createAgentDefinition(data)
      set((state) => ({
        agentDefinitions: [newDefinition, ...state.agentDefinitions],
      }))
      return newDefinition
    } catch (error) {
      logger.error('Failed to create agent definition', { error })
      throw error
    }
  },
  updateAgentDefinition: async (id: number, data: AgentDefinitionUpdate) => {
    try {
      const updated = await updateAgentDefinition(id, data)
      set((state) => ({
        agentDefinitions: state.agentDefinitions.map((d) => (d.id === id ? updated : d)),
      }))
      return updated
    } catch (error) {
      logger.error('Failed to update agent definition', { error })
      throw error
    }
  },
  deleteAgentDefinition: async (id: number) => {
    try {
      await deleteAgentDefinition(id)
      set((state) => ({
        agentDefinitions: state.agentDefinitions.filter((d) => d.id !== id),
      }))
    } catch (error) {
      logger.error('Failed to delete agent definition', { error })
      throw error
    }
  },
  fetchAvailableProviders: async () => {
    if (get().availableProviders.length > 0) return
    set({ availableProvidersLoading: true })
    try {
      const providers = await getAgentProviders()
      set({ availableProviders: providers, availableProvidersLoading: false })
    } catch (error) {
      logger.error('Failed to load providers', { error })
      set({ availableProvidersLoading: false })
    }
  },
  fetchAvailableTools: async () => {
    if (get().availableTools.length > 0) return
    set({ availableToolsLoading: true })
    try {
      const tools = await getAgentTools()
      set({ availableTools: tools, availableToolsLoading: false })
    } catch (error) {
      logger.error('Failed to load tools', { error })
      set({ availableToolsLoading: false })
    }
  },
  fetchAgentTemplates: async () => {
    if (get().agentTemplates.length > 0) return
    set({ agentTemplatesLoading: true })
    try {
      const templates = await getAgentTemplates()
      set({ agentTemplates: templates, agentTemplatesLoading: false })
    } catch (error) {
      logger.error('Failed to load templates', { error })
      set({ agentTemplatesLoading: false })
    }
  },
  fetchWorkspaceAgents: async (workspacePath: string) => {
    set({ workspaceAgentsLoading: true, workspaceAgentsError: null })
    try {
      const agents = await getWorkspaceAgents(workspacePath)
      set({ workspaceAgents: agents, workspaceAgentsLoading: false })
    } catch (error) {
      logger.error('Failed to load workspace agents', { error })
      set({
        workspaceAgentsError: error instanceof Error ? error.message : 'Failed to load workspace agents',
        workspaceAgentsLoading: false,
      })
    }
  },
})

